import {
  HydratedShopProduct,
  ShopAppearance,
  ShopAccordionItem,
  ShopBlock,
  ShopBlockWidth,
  ShopCta,
  ShopPage,
  ShopPageInfo,
  ShopPageSource,
  ShopProductLayout
} from 'models/shopPage';

import { GetShopPageRes } from './shopPage.types';

type JsonObject = Record<string, unknown>;

const isObject = (value: unknown): value is JsonObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const stringValue = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value : undefined;

const numberValue = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const normalizeWidth = (value: unknown, fallback: ShopBlockWidth = 'full'): ShopBlockWidth =>
  value === 'half' || value === 'full' ? value : fallback;

/** Keeps the first entry per key so duplicated authored ids never become duplicate React keys. */
const uniqueBy = <Item>(items: Item[], key: (item: Item) => string): Item[] => {
  const seen = new Set<string>();

  return items.filter((item) => {
    const id = key(item);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const normalizeAppearance = (value: unknown): ShopAppearance | undefined => {
  if (!isObject(value)) return undefined;

  const appearance = {
    accentColor: stringValue(value.accentColor),
    backgroundColor: stringValue(value.backgroundColor),
    backgroundImageUrl: stringValue(value.backgroundImageUrl),
    textColor: stringValue(value.textColor)
  };

  return Object.values(appearance).some(Boolean) ? appearance : undefined;
};

const normalizeCta = (value: unknown): ShopCta | undefined => {
  if (!isObject(value)) return undefined;
  const label = stringValue(value.label);
  const targetSlug = stringValue(value.targetSlug);
  const href = targetSlug ? `/shop/${targetSlug.replace(/^\/+/, '')}` : undefined;

  return label && href ? { href, label } : undefined;
};

const normalizeProduct = (value: unknown): HydratedShopProduct | null => {
  if (!isObject(value)) return null;
  const productId = stringValue(value.productId) ?? stringValue(value.id) ?? stringValue(value._id);
  const name = stringValue(value.name);
  const unitPrice = numberValue(value.unitPrice) ?? numberValue(value.price);
  if (!productId || !name || unitPrice === undefined) return null;

  return {
    badge: stringValue(value.badge) ?? stringValue(value.marketingBadge),
    categoryCode: stringValue(value.categoryCode),
    displayName: stringValue(value.displayName) ?? stringValue(value.displayNameOverride),
    imageUrl: stringValue(value.imageUrl) ?? stringValue(value.featuredAssetUrl),
    msrpPrice: numberValue(value.msrpPrice),
    name,
    productId,
    promoCopy: stringValue(value.promoCopy),
    requiresPrescription: value.requiresPrescription === true,
    subtitle: stringValue(value.subtitle),
    unitPrice
  };
};

const normalizeProductLayout = (value: unknown): ShopProductLayout => {
  switch (value) {
    case 'Cards':
    case 'Grid':
    case 'List':
    case 'ListHorizontal':
      return value;
    default:
      return 'Grid';
  }
};

const normalizeAccordionItem = (value: unknown): ShopAccordionItem | null => {
  if (!isObject(value)) return null;
  const id = stringValue(value.id);
  const title = stringValue(value.title);
  const html = stringValue(value.html) ?? stringValue(value.bodyHtml);
  return id && title && html ? { html, id, title } : null;
};

const normalizeBlock = (value: unknown): ShopBlock | null => {
  if (!isObject(value)) return null;
  const originalType = stringValue(value.type) ?? 'unknown';
  const id = stringValue(value.id);
  if (!id) return null;
  const appearance = normalizeAppearance({
    accentColor: value.accentColor,
    backgroundColor: value.backgroundColor,
    backgroundImageUrl: value.backgroundImageUrl,
    textColor: value.textColor,
    ...(isObject(value.appearance) ? value.appearance : {})
  });
  const width = normalizeWidth(value.width, originalType === 'featureCard' ? 'half' : 'full');
  const common = { appearance, id, width };

  switch (originalType) {
    case 'accordion': {
      const items = uniqueBy(
        Array.isArray(value.items)
          ? value.items.map(normalizeAccordionItem).filter((item) => item !== null)
          : [],
        ({ id }) => id
      );
      if (!items.length) return null;
      return {
        ...common,
        heading: stringValue(value.heading),
        items,
        type: 'accordion'
      };
    }
    case 'hero': {
      const headline = stringValue(value.headline) ?? stringValue(value.title);
      if (!headline) return null;
      return {
        ...common,
        cta: normalizeCta(value.cta),
        headline,
        imageAlt: stringValue(value.imageAlt),
        imageUrl: stringValue(value.imageUrl) ?? stringValue(value.mediaUrl),
        subcopy: stringValue(value.subcopy) ?? stringValue(value.body),
        type: 'hero'
      };
    }
    case 'featureCard': {
      const title = stringValue(value.title) ?? stringValue(value.headline);
      if (!title) return null;
      return {
        ...common,
        body: stringValue(value.body) ?? stringValue(value.copy),
        cta: normalizeCta(value.cta),
        eyebrow: stringValue(value.eyebrow),
        imageAlt: stringValue(value.imageAlt),
        imageUrl: stringValue(value.imageUrl) ?? stringValue(value.mediaUrl),
        title,
        type: 'featureCard'
      };
    }
    case 'productGrid': {
      const products = uniqueBy(
        Array.isArray(value.products)
          ? value.products.map(normalizeProduct).filter((product) => product !== null)
          : [],
        ({ productId }) => productId
      );
      if (!products.length) return null;
      return {
        ...common,
        heading: stringValue(value.heading),
        layout: normalizeProductLayout(value.layout),
        products,
        type: 'productGrid'
      };
    }
    case 'productSpotlight': {
      const product = normalizeProduct(value.product);
      if (!product) return null;
      return {
        ...common,
        badge: stringValue(value.badge),
        heading: stringValue(value.heading),
        marketingCopy: stringValue(value.marketingCopy),
        product,
        type: 'productSpotlight'
      };
    }
    case 'richText': {
      const html = stringValue(value.html);
      if (!html) return null;
      return { ...common, html, type: 'richText' };
    }
    default:
      return { ...common, originalType, type: 'unknown' };
  }
};

const normalizeSource = (value: unknown): ShopPageSource => {
  switch (value) {
    case 'fallback':
    case 'publishedSnapshot':
      return value;
    default:
      return 'publishedSnapshot';
  }
};

const normalizePage = (value: unknown): ShopPage => {
  const page = isObject(value) ? value : {};
  const blocks = uniqueBy(
    Array.isArray(page.blocks)
      ? page.blocks.map(normalizeBlock).filter((block) => block !== null)
      : [],
    ({ id }) => id
  );

  return {
    blocks,
    cohortTag: stringValue(page.cohortTag) ?? null,
    id: stringValue(page.id) ?? stringValue(page.pageId) ?? 'shop',
    pageRevision: numberValue(page.pageRevision) ?? 0,
    personalized: page.personalized === true,
    schemaVersion: numberValue(page.schemaVersion) ?? 1,
    slug: stringValue(page.slug) ?? 'shop',
    title: stringValue(page.title) ?? 'Shop'
  };
};

const normalizeInfo = (value: unknown): ShopPageInfo => {
  const info = isObject(value) ? value : {};
  return {
    degraded: info.degraded === true,
    source: normalizeSource(info.source)
  };
};

/**
 * Whitelists the patient-safe rendering contract. Audience rules, segment identifiers, traits,
 * synchronization metadata, and other authoring-only fields are intentionally not copied.
 */
export const normalizeShopPageResponse = (value: unknown): GetShopPageRes => {
  const response = isObject(value) ? value : {};
  return {
    data: normalizePage(response.data),
    errorCode: response.errorCode === 1 ? 1 : 0,
    info: normalizeInfo(response.info),
    message: stringValue(response.message) ?? ''
  };
};
